(() => {
  /*
    MOISLAND 全站倒计时 V3.3

    解决的问题：
    - 产品页在 Shopify 主题编辑器中预览时，必须读取当前草稿主题首页，
      不能误读已发布主题首页。
    - 首页时间暂时读取失败时自动重试，不再永久停留在 00。
    - 产品独立时间到期后，自动切换回首页倒计时。
  */

  const ROOT_SELECTOR = [
    '[data-global-promo-countdown]',
    '[data-moisland-global-countdown]'
  ].join(',');

  /*
    V3.3：
    同时兼容首页倒计时历代标签。
    当前首页使用 moisland-countdown-stable；
    旧版本可能使用 palo-countdown、palo-countdown-v51、
    palo-countdown-v53 等标签。
  */
  const HOMEPAGE_SOURCE_SELECTOR = [
    '[data-countdown-global-source][data-countdown-end]',
    '[data-global-countdown-source][data-end-at]',
    'moisland-countdown-stable[data-end-at]',
    'palo-countdown[data-end-at]',
    'palo-countdown-v51[data-end-at]',
    'palo-countdown-v52[data-end-at]',
    'palo-countdown-v53[data-end-at]',
    '.palo-countdown[data-end-at]',
    '[id^="MoislandCountdown-"][data-end-at]',
    '[id^="PaloCountdown-"][data-end-at]',
    '[data-source-class][data-end-at]'
  ].join(',');

  const CACHE_KEY =
    'moisland-homepage-countdown-deadline-v3-3';

  const GLOBAL_REFRESH_INTERVAL = 30000;
  const NULL_RETRY_INTERVAL = 5000;

  let timerId = 0;
  let refreshToken = 0;
  let globalDeadline = null;
  let globalRefreshPromise = null;
  let lastGlobalRefreshAt = 0;

  const parseDeadline = (value) => {
    if (!value) return null;

    const timestamp = Date.parse(String(value).trim());

    return Number.isFinite(timestamp)
      ? timestamp
      : null;
  };

  const getSourceValue = (source) => {
    if (!source) return '';

    return (
      source.dataset.countdownEnd ||
      source.dataset.endAt ||
      source.getAttribute('data-countdown-end') ||
      source.getAttribute('data-end-at') ||
      ''
    );
  };

  const readSourceDeadline = (
    documentRoot,
    now = Date.now()
  ) => {
    if (!documentRoot?.querySelectorAll) {
      return null;
    }

    const deadlines = Array.from(
      documentRoot.querySelectorAll(
        HOMEPAGE_SOURCE_SELECTOR
      )
    )
      .map((source) => parseDeadline(
        getSourceValue(source)
      ))
      .filter((deadline) => deadline !== null);

    if (!deadlines.length) {
      return null;
    }

    const futureDeadlines = deadlines
      .filter((deadline) => deadline > now)
      .sort((a, b) => a - b);

    if (futureDeadlines.length) {
      return futureDeadlines[0];
    }

    return deadlines.sort((a, b) => b - a)[0];
  };

  const readCachedDeadline = () => {
    try {
      const raw = window.localStorage.getItem(
        CACHE_KEY
      );

      if (!raw) return null;

      const cached = JSON.parse(raw);
      const deadline = parseDeadline(
        cached?.deadline
      );

      if (!deadline) return null;

      return deadline;
    } catch (error) {
      return null;
    }
  };

  const cacheDeadline = (deadline) => {
    if (!deadline) return;

    try {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          deadline,
          savedAt: Date.now()
        })
      );
    } catch (error) {
      // Storage can be unavailable in restricted browsers.
    }
  };

  const getShopifyRoot = () => (
    window.Shopify?.routes?.root ||
    document.documentElement.dataset.shopifyRoot ||
    '/'
  );

  const copyPreviewParameters = (
    targetUrl,
    currentUrl
  ) => {
    const exactKeys = new Set([
      'preview_theme_id',
      '_fd',
      'pb',
      'pbv',
      'previewPath',
      'theme',
      'theme_id',
      'locale',
      'country',
      'currency'
    ]);

    currentUrl.searchParams.forEach(
      (value, key) => {
        const lowerKey = key.toLowerCase();

        if (
          exactKeys.has(key) ||
          lowerKey.startsWith('preview') ||
          lowerKey.startsWith('_shopify')
        ) {
          targetUrl.searchParams.set(
            key,
            value
          );
        }
      }
    );

    const themeId =
      window.Shopify?.theme?.id;

    if (
      window.Shopify?.designMode &&
      themeId &&
      !targetUrl.searchParams.has(
        'preview_theme_id'
      )
    ) {
      targetUrl.searchParams.set(
        'preview_theme_id',
        String(themeId)
      );
    }

    return targetUrl;
  };

  const getHomepageUrls = () => {
    const rootUrl = new URL(
      getShopifyRoot(),
      window.location.origin
    );

    const currentUrl = new URL(
      window.location.href
    );

    const previewUrl = copyPreviewParameters(
      new URL(rootUrl.toString()),
      currentUrl
    );

    const urls = [
      previewUrl.toString(),
      rootUrl.toString()
    ];

    return [...new Set(urls)];
  };

  const fetchDeadlineFromUrl = async (
    homepageUrl
  ) => {
    const response = await fetch(homepageUrl, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Homepage request failed: ${response.status}`
      );
    }

    const html = await response.text();

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    return readSourceDeadline(
      parsedDocument
    );
  };

  const fetchHomepageDeadline = async () => {
    const directDeadline =
      readSourceDeadline(document);

    if (directDeadline) {
      cacheDeadline(directDeadline);
      return directDeadline;
    }

    const homepageUrls = getHomepageUrls();
    let lastError = null;

    for (const homepageUrl of homepageUrls) {
      try {
        const deadline =
          await fetchDeadlineFromUrl(
            homepageUrl
          );

        if (deadline) {
          cacheDeadline(deadline);
          return deadline;
        }
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) {
      console.warn(
        'MOISLAND countdown could not read the homepage end time.',
        lastError
      );
    }

    return readCachedDeadline();
  };

  const refreshGlobalDeadline = async ({
    force = false
  } = {}) => {
    const now = Date.now();

    if (
      !force &&
      globalRefreshPromise
    ) {
      return globalRefreshPromise;
    }

    const retryInterval = globalDeadline
      ? GLOBAL_REFRESH_INTERVAL
      : NULL_RETRY_INTERVAL;

    if (
      !force &&
      now - lastGlobalRefreshAt < retryInterval
    ) {
      return globalDeadline;
    }

    lastGlobalRefreshAt = now;

    globalRefreshPromise = (
      async () => {
        const directDeadline =
          readSourceDeadline(document);

        const resolvedDeadline =
          directDeadline ||
          await fetchHomepageDeadline();

        if (resolvedDeadline) {
          globalDeadline =
            resolvedDeadline;

          cacheDeadline(
            resolvedDeadline
          );
        }

        return globalDeadline;
      }
    )();

    try {
      return await globalRefreshPromise;
    } finally {
      globalRefreshPromise = null;
    }
  };

  const writeValue = (
    root,
    selector,
    value
  ) => {
    root
      .querySelectorAll(selector)
      .forEach((element) => {
        element.textContent =
          String(value).padStart(2, '0');
      });
  };

  const setVisibility = (
    root,
    expired
  ) => {
    [
      '[data-countdown-running]',
      '[data-countdown-running-content]'
    ].forEach((selector) => {
      root
        .querySelectorAll(selector)
        .forEach((element) => {
          element.hidden = expired;
        });
    });

    [
      '[data-countdown-expired]',
      '[data-countdown-expired-content]'
    ].forEach((selector) => {
      root
        .querySelectorAll(selector)
        .forEach((element) => {
          element.hidden = !expired;
        });
    });

    root.hidden =
      expired &&
      root.dataset.hideWhenExpired === 'true';
  };

  const updateRoot = (
    root,
    now
  ) => {
    const configuredDeadline =
      parseDeadline(
        root.dataset.countdownEnd
      );

    const isProductOverride =
      root.dataset.countdownSource ===
      'product';

    const productOverrideIsActive =
      isProductOverride &&
      configuredDeadline &&
      configuredDeadline > now;

    let deadline = null;
    let activeSource = 'waiting';

    if (productOverrideIsActive) {
      deadline = configuredDeadline;
      activeSource = 'product';
    } else if (globalDeadline) {
      deadline = globalDeadline;
      activeSource = 'homepage';
    } else if (configuredDeadline) {
      deadline = configuredDeadline;
      activeSource = isProductOverride
        ? 'product'
        : 'local';
    }

    if (!deadline) {
      root.dataset.countdownState =
        'waiting-for-homepage';

      root.dataset.activeCountdownSource =
        'waiting';

      root.hidden = false;

      /*
        暂时找不到首页时间时不判定为已结束。
        脚本会每 5 秒重新读取当前草稿主题首页。
      */
      return;
    }

    const remaining = Math.max(
      0,
      deadline - now
    );

    const totalSeconds = Math.floor(
      remaining / 1000
    );

    const days = Math.floor(
      totalSeconds / 86400
    );

    const hours = Math.floor(
      (totalSeconds % 86400) / 3600
    );

    const minutes = Math.floor(
      (totalSeconds % 3600) / 60
    );

    const seconds =
      totalSeconds % 60;

    writeValue(
      root,
      '[data-countdown-days]',
      days
    );

    writeValue(
      root,
      '[data-countdown-hours]',
      hours
    );

    writeValue(
      root,
      '[data-countdown-minutes]',
      minutes
    );

    writeValue(
      root,
      '[data-countdown-seconds]',
      seconds
    );

    const expired = remaining <= 0;

    root.dataset.countdownState =
      expired ? 'expired' : 'running';

    root.dataset.activeCountdownSource =
      activeSource;

    setVisibility(root, expired);
  };

  const tick = (token) => {
    if (token !== refreshToken) return;

    const now = Date.now();

    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        updateRoot(root, now);
      });

    const retryInterval = globalDeadline
      ? GLOBAL_REFRESH_INTERVAL
      : NULL_RETRY_INTERVAL;

    if (
      now - lastGlobalRefreshAt >=
      retryInterval
    ) {
      refreshGlobalDeadline().then(() => {
        document
          .querySelectorAll(ROOT_SELECTOR)
          .forEach((root) => {
            updateRoot(
              root,
              Date.now()
            );
          });
      });
    }

    window.clearTimeout(timerId);

    timerId = window.setTimeout(
      () => tick(token),
      1000 - (Date.now() % 1000)
    );
  };

  const refresh = async ({
    reloadHomepage = false
  } = {}) => {
    refreshToken += 1;
    const token = refreshToken;

    window.clearTimeout(timerId);
    timerId = 0;

    const directDeadline =
      readSourceDeadline(document);

    globalDeadline =
      directDeadline ||
      readCachedDeadline() ||
      globalDeadline;

    tick(token);

    await refreshGlobalDeadline({
      force: reloadHomepage ||
        !globalDeadline
    });

    if (token !== refreshToken) return;

    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        updateRoot(
          root,
          Date.now()
        );
      });
  };

  window.MoislandGlobalCountdown = {
    refresh: () => refresh({
      reloadHomepage: true
    }),

    getDeadline: () =>
      globalDeadline
  };

  const start = () => refresh({
    reloadHomepage: true
  });

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    () => refresh({
      reloadHomepage: true
    })
  );

  document.addEventListener(
    'shopify:section:reorder',
    () => refresh({
      reloadHomepage: true
    })
  );

  document.addEventListener(
    'shopify:block:select',
    () => refresh({
      reloadHomepage: true
    })
  );

  window.addEventListener(
    'pageshow',
    () => refresh({
      reloadHomepage: true
    })
  );
})();
